import rp, { SqlCompletionOptions, SqlEditorAdapter, SqlEditorOptions, SqlExecOptions, SqlExecCallbackArgs, SqlParseResult } from "@reporting/rp-app-host";
import { msg } from "@omegagrid/localize";
import { AccountingRights } from "@reporting/rp-types/core";
import { DwhObjectType } from "../types";

export type DwhSqlEditorMode = 'object' | 'processBuffer';

export class DwhSqlEditorAdapter extends SqlEditorAdapter {

	constructor(
		private mode: DwhSqlEditorMode,
		private objectType: DwhObjectType | null,
		private id: string,
	) { super() }

	async getContent(): Promise<string> {
		if (this.mode === 'processBuffer') {
			const result = await rp.ms('server').get<{ buffer: string | null }>(`processBuffer/${this.id}`);
			return result?.buffer ?? '';
		}
		const result = await rp.ms('server').get<{ script: string | null }>(this.contentEndpoint());
		const script = result?.script ?? '';
		switch (this.objectType) {
			case 'procedure': return this.changeToAlter(script, 'procedure');
			case 'function': return this.changeToAlter(script, 'function');
			case 'trigger': return this.changeToAlter(script, 'trigger');
			case 'view': return this.changeToAlter(script, 'view');
			default: return script;
		}
	}

	private changeToAlter(sql: string, what: string): string {
		return sql.replace(new RegExp(`create\s+${what}`, 'i'), `alter ${what}`);
	}

	private contentEndpoint(): string {
		switch (this.objectType) {
			case 'procedure': return `procedureContent/${this.id}`;
			case 'function': return `functionContent/${this.id}`;
			case 'trigger': return `triggerContent/${this.id}`;
			case 'view': return `viewContent/${this.id}`;
			case 'index': return `indexContent/${this.id}`;
			default: throw new Error(`Unknown DWH object type: ${this.objectType}`);
		}
	}

	async parse(sql: string): Promise<SqlParseResult> {
		return rp.ms('server').post<SqlParseResult>('parse', sql);
	}

	async exec(sql: string, options?: SqlExecOptions, cb?: (args: SqlExecCallbackArgs) => void): Promise<void> {
		const received = await this.execStream(rp.ms('server'), 'exec', sql, options, cb);
		if (!received && cb) {
			cb({ message: { index: 0, content: msg('Completed successfully.') } });
		}
		this.component.dispatchEvent(new CustomEvent('save', { bubbles: true }));
	}

	save(): void { /* DWH SQL editor is read-only on script source */ }

	/** rp-ms-server runs the text as written: no engine @variables, {placeholders} or {{macros}} */
	getCompletionOptions(): SqlCompletionOptions {
		return { internalVariables: false, pseudoVariables: false, macros: false };
	}

	async abort(): Promise<void> {
		await this.abortSpid(rp.ms('server'));
	}

	getOptions(): SqlEditorOptions {
		const readOnly = this.mode === 'processBuffer' || !rp.userProvider.hasRight(AccountingRights.DWHWrite);
		return this.createOptions({
			readOnly: readOnly,
			toolbar: {
				save: { enabled: false },
				discard: { enabled: false },
				compile: { enabled: false },
			},
		});
	}

}
